import React, {useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
// @ts-ignore
import {useTable, Column} from "react-table";
import * as actions from '../../_redux/actions/popupPriceCatalogActions';
import * as columnFormatters from '../../../_common/column-formatters';
import {usePopupPriceCatalogContext} from '../../contexts/PopupPriceCatalogContext';
import useInfiniteScrollControl from '../../../_common/components/_custom-hooks/useInfiniteScrollControl';
import CommonGrid from '../../../_common/components/CommonGrid';
import CommonGridSpinner from '../../../_common/components/CommonGridSpinner';
import styles from '../styles.module.scss';

type PriceCatalogItem = {
  mainCategory: string;
  secondCategory: string;
  serviceDescription: string;
  servicePrice: number;
  notes: string;
};

type PriceCatalogRow = {
  mainCategory: React.ReactNode;
  secondCategory: React.ReactNode;
  serviceDescription: React.ReactNode;
  servicePrice: React.ReactNode;
  notes: React.ReactNode;
  actions: React.ReactNode;
};

export function PriceCatalogTable() {
  const {formatMessage} = useIntl();
  const [tableRows, setTableRows] = useState<PriceCatalogItem[]>([]);

  const prefix = 'price-quote-details-popup-price-catalog-column';

  const columns: Column<PriceCatalogRow>[] = useMemo(() => [
      {
        accessor: 'mainCategory',
        Header: formatMessage({id: `${prefix}-mainCategory`, defaultMessage: 'Main category'}),
      },
      {
        accessor: 'secondCategory',
        Header: formatMessage({id: `${prefix}-secondCategory`, defaultMessage: 'Second category'}),
      },
      {
        accessor: 'serviceDescription',
        Header: formatMessage({id: `${prefix}-serviceDescription`, defaultMessage: 'Service description'}),
        width: 400
      },
      {
        accessor: 'servicePrice',
        Header: formatMessage({id: `${prefix}-servicePrice`, defaultMessage: 'Service price'}),
      },
      {
        accessor: 'notes',
        Header: formatMessage({id: `${prefix}-notes`, defaultMessage: 'Notes'}),
      },
      {
        accessor: 'actions',
        Header: '',
        width: 90,
      },
    ],
    [formatMessage]
  );

  const data: PriceCatalogRow[] = useMemo(
    () =>
      tableRows.map((item) => ({
        mainCategory: columnFormatters.BoldColumnFormatter(item.mainCategory),
        secondCategory: columnFormatters.BoldColumnFormatter(item.secondCategory),
        serviceDescription: columnFormatters.BoldColumnFormatter(item.serviceDescription),
        servicePrice: columnFormatters.PriceColumnFormatter(item.servicePrice),
        notes: columnFormatters.BoldColumnFormatter(item.notes),
        actions: columnFormatters.PriceCatalogActionColumnFormatter(item),
      })),
    [tableRows]
  );
  const popupPriceCatalogContext = usePopupPriceCatalogContext();

  const infiniteScrollProps = useInfiniteScrollControl({
    currentStateStoreId: 'popupPriceCatalogTableReducer',
    UIContext: popupPriceCatalogContext,
    getNewEntities: actions.getPriceCatalogItems,
    getNextEntities: actions.getNextPriceCatalogItems,
    onEntitiesChange: (entities?: PriceCatalogItem[]) => {
      setTableRows(entities ?? []);
    },
    startRequestedPageNumber: 0,
    dataLength: tableRows.length,
    scrollableTarget: 'scrollContainer',
    scrollThreshold: 0.9,
    loader: <CommonGridSpinner/>,
    style: {
      overflow: 'inherit',
    },
  });

  const tableInstance = useTable<PriceCatalogRow>({
    columns,
    data,
  });

  return (
    <CommonGrid
      infiniteScrollProps={infiniteScrollProps}
      customScrollContainerStyles={styles.priceCatalogTableContainer}
      {...tableInstance}
    />
  );
}

export default PriceCatalogTable;